import classNames from "classnames";
import { FC, PropsWithChildren, ReactNode, useState } from "react";
import { NavLink, To, useLocation } from "react-router-dom";
import { Glyph } from "../../types";
import SvgArrowDropDown from "../icons/ArrowDropDown";
import SvgOpenInNew from "../icons/OpenInNew";
import Icon from "./Icon";
import styles from "./Nav.module.css";
import NumberError from "./NumberError";

const NavProvider = ({ children }: PropsWithChildren) => (
  <div className={styles.nav}>
    <div className={styles.logo}>
      <img src={window.location.origin + "/logo.svg"} />
    </div>
    <div className={styles.scroll}>{children}</div>
    {/* <NeedsAttentionCard /> */}
  </div>
);

type NavListProps = PropsWithChildren & { className?: string };

const NavList = ({ className, children }: NavListProps) => (
  <ul className={classNames(styles.ul, className)}>{children}</ul>
);

const NavGroup = ({ title, children }: PropsWithChildren<{ title: string }>) => (
  <li className={styles.group}>
    <span className={styles.title}>{title}</span>
    {children && <NavList>{children}</NavList>}
  </li>
);

export type NavItemNotification = {
  color: "red" | "yellow";
  number?: number;
  onClick?: () => void;
};

type NavItemProps = {
  text: string;
  icon?: Glyph;
  to?: To;
  target?: React.HTMLAttributeAnchorTarget;
  onClick?: () => void;
  children?: ReactNode;
  notification?: NavItemNotification;
  groupId?: string;
};

const NavItem: FC<NavItemProps> = ({ text, icon, to, target = "_self", onClick, children, notification, groupId }) => {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(!!groupId && localStorage.getItem(groupId) === "true");

  const isActive = (link: To): boolean => {
    const pathname = (p: string) => {
      try {
        return new URL(p, window.location.origin).pathname.replace(/\/+$/, "");
      } catch {
        return p.split("?")[0]?.split("#")[0]?.replace(/\/+$/, "");
      }
    };
    const loc = pathname(location.pathname)?.split("/");
    const dest = pathname(link.toString())?.split("/");
    if (loc?.at(-1) === dest?.at(-1) || dest?.at(-1) === loc?.at(-2)) return true;
    if (dest?.at(-1) === loc?.at(-3) && ["costs_imports", "my_cars", "my_team_cars"].includes(dest?.at(-1) ?? "")) {
      return true;
    }
    return false;
  };

  const toggle = () => {
    setIsOpen((prev) => {
      const next = !prev;
      if (groupId) localStorage.setItem(groupId, String(next));
      return next;
    });
  };

  const active = !!to && isActive(to);
  const badgeCount = notification?.number ?? 0;

  const linkClassName = classNames(styles.link, {
    [styles["with-children"]]: !!children,
    [styles.active]: active,
  });

  const badge =
    notification?.color && badgeCount > 0 ? (
      <NumberError
        number={badgeCount}
        className={styles.badge}
        color={notification.color}
        onClick={notification.onClick}
      />
    ) : null;

  const itemContent = (
    <>
      {icon && <Icon glyph={icon} className={styles.icon} />}
      {text}
      {badge}
      {target === "_blank" && <Icon glyph={SvgOpenInNew} className={styles["open-in-new"]} />}
    </>
  );

  return (
    <>
      <li
        className={classNames(styles.li, {
          [styles.active]: active,
          [styles.red]: notification?.color === "red",
          [styles.yellow]: notification?.color === "yellow",
        })}
      >
        {to ? (
          <NavLink to={to} target={target} onClick={() => onClick?.()} className={linkClassName}>
            {itemContent}
          </NavLink>
        ) : (
          <div
            onClick={() => {
              onClick?.();
              toggle();
            }}
            className={classNames(linkClassName, styles.clickable)}
          >
            {itemContent}
          </div>
        )}
        {children && (
          <button
            className={classNames(styles.button, {
              [styles.active]: active,
            })}
            onClick={toggle}
          >
            <Icon glyph={SvgArrowDropDown} className={classNames(styles.arrowDropDown, { [styles.isOpen]: isOpen })} />
          </button>
        )}
      </li>
      {children && (
        <div className={classNames(styles["children-wrapper"], { [styles.open]: isOpen })}>
          <div className={styles["children-inner"]}>{children}</div>
        </div>
      )}
    </>
  );
};

NavProvider.Group = NavGroup;
NavProvider.List = NavList;
NavProvider.Item = NavItem;

export default NavProvider;
